import { Injectable, inject } from '@angular/core';

import { OddsFormat } from '@bpos';
import { MessageEnvelope } from '@cds/push';
import { OddsOperations } from '@frontend/sports/odds/feature';
import { UserOddsFormatSettingsService } from '@frontend/sports/user/feature';

import { BaseBetslipGroupPick } from '../../../core/picks/betslip-group-pick';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { BetslipV1Pick } from '../../../core/picks/betslip-v1-pick';
import { BetslipV2OptionMarketPick } from '../../../core/picks/betslip-v2-option-market-pick';
import { BetslipV2ParticipantPick } from '../../../core/picks/betslip-v2-participant-pick';
import { BetslipV2WinParticipantPick } from '../../../core/picks/betslip-v2-win-participant-pick';
import { PickUpdateType } from '../../../core/picks/pick-models';
import { BaseBetslipGroupPickUpdater } from './base-betslip-group-pick.updater';
import { BetslipV1PickUpdater } from './betslip-v1-pick.updater';
import { BetslipV2OptionMarketPickUpdater } from './betslip-v2-option-market-pick.updater';
import { BetslipV2ParticipantPickUpdater } from './betslip-v2-participant-pick.updater';
import { BetslipV2WinParticipantPickUpdater } from './betslip-v2-win-participant-pick.updater';

@Injectable({ providedIn: 'root' })
export default class PickPushUpdaterService {
    private readonly userOddsFormatSettingsService = inject(UserOddsFormatSettingsService);

    get userFormat() {
        return OddsOperations.convertToBposOddsFormat(this.userOddsFormatSettingsService.oddsFormat);
    }

    updatePick<TPick extends BetslipPick>(pick: TPick, message: MessageEnvelope): [TPick, PickUpdateType] {
        const newPick = PickPushUpdaterService.updatePickBase(pick, message, this.userFormat);
        const updateType = this.findUpdateType(pick, newPick);

        return [newPick, updateType];
    }

    static updatePickBase<TPick extends BetslipPick>(pick: TPick, message: MessageEnvelope, oddsFormat: OddsFormat): TPick {
        let newPick = pick;
        if (BetslipV1Pick.isPick(pick)) {
            newPick = BetslipV1PickUpdater.updateV1Pick(pick, message, oddsFormat) as TPick;
        } else if (BetslipV2OptionMarketPick.isPick(pick)) {
            newPick = BetslipV2OptionMarketPickUpdater.updateV2OptionMarketPick(pick, message, oddsFormat) as TPick;
        } else if (BetslipV2ParticipantPick.isPick(pick)) {
            const updater = BetslipV2WinParticipantPick.isPick(pick)
                ? new BetslipV2WinParticipantPickUpdater()
                : new BetslipV2ParticipantPickUpdater();
            newPick = updater.updateFromMessage(pick, message, oddsFormat) as TPick;
        } else if (BaseBetslipGroupPick.isPick(pick)) {
            newPick = BaseBetslipGroupPickUpdater.update(pick, message, oddsFormat) as TPick;
        }

        return newPick;
    }

    findUpdateType(originalPick: BetslipPick, updatedPick: BetslipPick): PickUpdateType {
        if (BaseBetslipGroupPick.isPick(originalPick) && BaseBetslipGroupPick.isPick(updatedPick)) {
            return this.findUpdateTypeForBetBuilderGroups(originalPick, updatedPick);
        }

        return this.findUpdateTypeForClassicPicks(originalPick, updatedPick);
    }

    private findUpdateTypeForBetBuilderGroups(originalPick: BaseBetslipGroupPick, updatedPick: BaseBetslipGroupPick): PickUpdateType {
        let updateType = PickUpdateType.None;

        if (updatedPick.isMarketVisible() !== originalPick.isMarketVisible()) {
            // When picks has different market visibility
            // eslint-disable-next-line no-bitwise
            updateType |= PickUpdateType.MarketVisibility;
        }

        if (updatedPick.currentPrice && originalPick.currentPrice) {
            // When both picks has prices
            if (updatedPick.currentPrice.isVisible !== originalPick.currentPrice.isVisible) {
                // If price visibility is different
                // eslint-disable-next-line no-bitwise
                updateType |= PickUpdateType.PriceVisibility;
            } else if (
                updatedPick.currentPrice.isVisible &&
                originalPick.currentPrice.isVisible &&
                !OddsOperations.equal(updatedPick.currentPrice.nativeOdds, originalPick.currentPrice.nativeOdds)
            ) {
                // If both prices are visible but have different odds
                // eslint-disable-next-line no-bitwise
                updateType |= PickUpdateType.Odds;
            }
        } else if (!updatedPick.currentPrice !== !updatedPick.currentPrice) {
            // When one of the picks has price but the other not.
            // eslint-disable-next-line no-bitwise
            updateType |= PickUpdateType.PriceVisibility;
        }

        return updateType === PickUpdateType.None ? PickUpdateType.Unknown : updateType;
    }

    private findUpdateTypeForClassicPicks(originalPick: BetslipPick, updatedPick: BetslipPick): PickUpdateType {
        let updateType = PickUpdateType.None;
        if (originalPick === updatedPick) {
            // When picks are the same then no update
            return updateType;
        }
        if (updatedPick.market.isVisible !== originalPick.market.isVisible) {
            // When picks has different market visibility
            // eslint-disable-next-line no-bitwise
            updateType |= PickUpdateType.MarketVisibility;
        }
        if (updatedPick.currentPrice && originalPick.currentPrice) {
            // When both picks have prices
            if (updatedPick.currentPrice.isVisible !== originalPick.currentPrice.isVisible) {
                // If price visibility is different
                // eslint-disable-next-line no-bitwise
                updateType |= PickUpdateType.PriceVisibility;
            } else if (
                updatedPick.currentPrice.isVisible &&
                originalPick.currentPrice.isVisible &&
                !OddsOperations.equal(updatedPick.currentPrice.nativeOdds, originalPick.currentPrice.nativeOdds)
            ) {
                // If both prices are visible but have different odds
                // eslint-disable-next-line no-bitwise
                updateType |= PickUpdateType.Odds;
            }
        } else if (!updatedPick.currentPrice !== !updatedPick.currentPrice) {
            // When one of the picks has price but the other not.
            // eslint-disable-next-line no-bitwise
            updateType |= PickUpdateType.PriceVisibility;
        }

        return updateType === PickUpdateType.None ? PickUpdateType.Unknown : updateType;
    }
}
